import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle, AlertCircle, XCircle, RefreshCw, Loader2, RotateCcw, Clock } from 'lucide-react';
import { ticketAlertsAPI } from '../../../services/api';

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

function Dot({ ok, warn }) {
  if (ok) return <CheckCircle className="h-4 w-4 text-green-600" />;
  if (warn) return <AlertCircle className="h-4 w-4 text-amber-500" />;
  return <XCircle className="h-4 w-4 text-red-500" />;
}

const OUTCOME_STYLES = {
  posted: 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  updated: 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  skipped: 'bg-neutral-100 text-neutral-600 dark:bg-neutral-700 dark:text-neutral-300',
  received: 'bg-neutral-100 text-neutral-600 dark:bg-neutral-700 dark:text-neutral-300',
  // Some platforms delivered and some did not. Amber rather than red: the
  // ticket did reach somebody, and reading it as a total failure is what sent
  // somebody looking for a Slack message that had been there all along.
  partial: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  // A rate limit or a 5xx on one platform, with another attempt queued for
  // that platform only. Not yet delivered, not yet failed.
  retrying: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  error: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300',
};

/**
 * Ongoing health: Slack installation, registered CW callbacks, outbox, and
 * the recent event log. Polls every 30s while open.
 */
export default function TicketAlertsStatusPanel({ overview, onReregister, reregistering }) {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['ticket-alerts-events'],
    queryFn: () => ticketAlertsAPI.events(50),
    refetchInterval: 30000,
    select: (resp) => resp.data,
  });

  const retryMutation = useMutation({
    mutationFn: (id) => ticketAlertsAPI.retryJob(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['ticket-alerts-events'] }),
  });

  // Every connected platform, not just Slack: a Google Chat or Teams
  // connection going bad was invisible on this panel.
  const chatPlatforms = [
    { key: 'slack', label: 'Slack', installation: overview?.slack?.installation },
    { key: 'gchat', label: 'Google Chat', installation: overview?.gchat?.installation },
    { key: 'teams', label: 'Microsoft Teams', installation: overview?.teams?.installation },
  ].filter((p) => p.installation);
  const callbacks = overview?.callbacks?.callbacks || [];
  const remoteError = overview?.callbacks?.remoteError;
  const jobs = data?.jobs || overview?.jobs;
  const events = data?.events || [];
  const failedJobs = data?.failedJobs || [];
  const lastEvent = events[0];

  return (
    <div className="space-y-6">
      {/* Summary tiles */}
      <div className="grid gap-3 sm:grid-cols-3">
        {chatPlatforms.length === 0 && (
          <div className="rounded-lg border border-neutral-200 p-3 dark:border-neutral-700">
            <div className="flex items-center gap-2 text-sm font-medium text-neutral-900 dark:text-white">
              <Dot warn />
              Chat
            </div>
            <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">No chat platform connected</p>
          </div>
        )}
        {chatPlatforms.map((p) => (
          <div key={p.key} className="rounded-lg border border-neutral-200 p-3 dark:border-neutral-700">
            <div className="flex items-center gap-2 text-sm font-medium text-neutral-900 dark:text-white">
              <Dot ok={!p.installation.lastError} warn={!!p.installation.lastError} />
              {p.label}
            </div>
            <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
              {p.installation.workspaceName} · connected {fmt(p.installation.installedAt)}
            </p>
            {p.installation.lastError && (
              <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                {p.installation.lastError} ({fmt(p.installation.lastErrorAt)})
              </p>
            )}
          </div>
        ))}
        <div className="rounded-lg border border-neutral-200 p-3 dark:border-neutral-700">
          <div className="flex items-center gap-2 text-sm font-medium text-neutral-900 dark:text-white">
            <Dot
              ok={callbacks.length > 0 && callbacks.every((c) => c.status === 'active' && c.presentInCw !== false)}
              warn={callbacks.length === 0 || !!remoteError}
            />
            ConnectWise callbacks
          </div>
          <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
            {callbacks.length} registered · last event {fmt(lastEvent?.created_at)}
          </p>
          {remoteError && <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">Could not verify in CW: {remoteError}</p>}
        </div>
        <div className="rounded-lg border border-neutral-200 p-3 dark:border-neutral-700">
          <div className="flex items-center gap-2 text-sm font-medium text-neutral-900 dark:text-white">
            <Dot ok={!jobs || jobs.failed === 0} warn={jobs && jobs.failed > 0} />
            Background queue
          </div>
          <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
            {jobs ? `${jobs.pending} pending · ${jobs.running} running · ${jobs.failed} failed` : '—'}
          </p>
        </div>
      </div>

      {/* Callbacks table */}
      <div>
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-neutral-900 dark:text-white">Registered callbacks</h3>
          <button
            type="button"
            onClick={onReregister}
            disabled={reregistering}
            className="inline-flex items-center gap-1 rounded-lg border border-neutral-300 px-3 py-1.5 text-xs text-neutral-700 hover:bg-neutral-50 disabled:opacity-50 dark:border-neutral-600 dark:text-neutral-200 dark:hover:bg-neutral-700"
          >
            {reregistering ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            Re-register callbacks
          </button>
        </div>
        {callbacks.length === 0 ? (
          <p className="text-sm text-neutral-500 dark:text-neutral-400">None yet. Save the configuration with at least one board while enabled.</p>
        ) : (
          <div className="max-h-96 overflow-auto rounded-lg border border-neutral-200 dark:border-neutral-700">
            <table className="min-w-full text-sm">
              <thead className="bg-neutral-50 text-left text-xs uppercase text-neutral-500 dark:bg-neutral-900/40 dark:text-neutral-400">
                <tr>
                  <th className="px-3 py-2">Board</th>
                  <th className="px-3 py-2">CW callback</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Registered</th>
                  <th className="px-3 py-2">Last event</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-200 dark:divide-neutral-700">
                {callbacks.map((c) => (
                  <tr key={c.boardId}>
                    <td className="px-3 py-2 text-neutral-900 dark:text-white">{c.boardName || c.boardId}</td>
                    <td className="px-3 py-2 text-neutral-500">{c.cwCallbackId || '—'}</td>
                    <td className="px-3 py-2">
                      <span className="inline-flex items-center gap-1">
                        <Dot ok={c.status === 'active' && c.presentInCw !== false && !c.inactiveInCw} warn={c.presentInCw === null} />
                        {c.status === 'active'
                          ? c.presentInCw === false
                            ? 'Missing in CW'
                            : c.inactiveInCw
                              ? 'Inactive in CW'
                              : 'Active'
                          : c.status}
                        {c.lastError && <span className="ml-1 text-xs text-red-600">{c.lastError}</span>}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-neutral-500">{fmt(c.registeredAt)}</td>
                    <td className="px-3 py-2 text-neutral-500">{fmt(c.lastEventAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Failed jobs */}
      {failedJobs.length > 0 && (
        <div>
          <h3 className="mb-2 text-sm font-semibold text-neutral-900 dark:text-white">Failed background jobs</h3>
          <ul className="space-y-2">
            {failedJobs.map((j) => (
              <li key={j.id} className="flex items-start justify-between gap-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm dark:border-red-800 dark:bg-red-900/20">
                <div className="min-w-0">
                  <div className="font-medium text-neutral-900 dark:text-white">
                    {j.kind} · ticket #{j.payload?.ticketId} · {j.attempts} attempts
                  </div>
                  <div className="truncate text-xs text-red-700 dark:text-red-300">{j.last_error}</div>
                  <div className="text-xs text-neutral-500">{fmt(j.finished_at)}</div>
                </div>
                <button
                  type="button"
                  onClick={() => retryMutation.mutate(j.id)}
                  className="inline-flex shrink-0 items-center gap-1 text-xs text-primary-600 hover:underline dark:text-primary-400"
                >
                  <RotateCcw className="h-3.5 w-3.5" /> Retry
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Recent events */}
      <div>
        <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-neutral-900 dark:text-white">
          <Clock className="h-4 w-4 text-neutral-400" /> Recent events
          {events.length > 0 && (
            <span className="font-normal text-neutral-500 dark:text-neutral-400">
              last {events.length}, kept for 30 days
            </span>
          )}
          {isLoading && <Loader2 className="h-3.5 w-3.5 animate-spin text-neutral-400" />}
        </h3>
        {/* The table scrolls rather than growing. The API returns fifty, which
            laid out in full is most of two screens and pushes everything below
            it off the page. Fifty is also where it stops: rows older than
            thirty days are deleted by the outbox's own maintenance pass. */}
        {events.length === 0 ? (
          <p className="text-sm text-neutral-500 dark:text-neutral-400">Nothing received yet.</p>
        ) : (
          <div className="max-h-96 overflow-auto rounded-lg border border-neutral-200 dark:border-neutral-700">
            <table className="min-w-full text-sm">
              <thead className="sticky top-0 z-10 bg-neutral-50 text-left text-xs uppercase text-neutral-500 shadow-sm dark:bg-neutral-900 dark:text-neutral-400">
                <tr>
                  <th className="px-3 py-2">When</th>
                  <th className="px-3 py-2">Ticket</th>
                  <th className="px-3 py-2">Action</th>
                  <th className="px-3 py-2">Events</th>
                  <th className="px-3 py-2">Outcome</th>
                  <th className="px-3 py-2">Detail</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-200 dark:divide-neutral-700">
                {events.map((e) => (
                  <tr key={e.id}>
                    <td className="whitespace-nowrap px-3 py-2 text-neutral-500">{fmt(e.created_at)}</td>
                    <td className="px-3 py-2 text-neutral-900 dark:text-white">#{e.ticket_id}</td>
                    <td className="px-3 py-2 text-neutral-500">{e.action}</td>
                    <td className="px-3 py-2 text-neutral-500">{(e.event_types || []).join(', ') || '—'}</td>
                    <td className="px-3 py-2">
                      <span className={`rounded-full px-2 py-0.5 text-xs ${OUTCOME_STYLES[e.outcome] || OUTCOME_STYLES.received}`}>{e.outcome}</span>
                    </td>
                    <td className="max-w-xs truncate px-3 py-2 text-xs text-neutral-500" title={e.detail || ''}>
                      {e.detail || ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
